import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Loader2, GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../../lib/api";
import { useConfirm } from "./_shared";

/**
 * FAQTab — full CRUD for the /faq page content.
 * Add / edit / delete / reorder questions. Changes go live instantly on the
 * public /faq page (customers see them without a redeploy).
 */
export default function FAQTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(-1);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/faq")
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load FAQ"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  const addNew = async () => {
    const nextOrder = items.length ? Math.max(...items.map((i) => i.order || 0)) + 1 : 0;
    try {
      const { data } = await api.post("/admin/faq", {
        question: "New question — click Edit to update",
        answer: "New answer content.",
        order: nextOrder,
      });
      toast.success("Added");
      setItems((cur) => [...cur, data]);
      setOpenIdx(items.length);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id, question) => {
    const ok = await confirm({
      title: "Delete this FAQ item?",
      message: "It will vanish from the public /faq page immediately.",
      meta: question,
      confirmLabel: "Delete FAQ",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/faq/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[i], b = items[j];
    try {
      await Promise.all([
        api.patch(`/admin/faq/${a.id}`, { question: a.question, answer: a.answer, order: b.order }),
        api.patch(`/admin/faq/${b.id}`, { question: b.question, answer: b.answer, order: a.order }),
      ]);
      load();
    } catch (e) { toast.error("Reorder failed"); }
  };

  return (
    <div data-testid="bo-faq-tab" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps text-electric">FAQ Editor · public /faq page</div>
          <h2 className="font-serif text-2xl mt-1">
            {items.length} question{items.length === 1 ? "" : "s"} live
          </h2>
          <p className="text-white/50 text-xs mt-1">
            Edits go live on customers&apos; screens the moment you save — no redeploy needed.
          </p>
        </div>
        <button
          type="button"
          onClick={addNew}
          data-testid="bo-faq-add"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric text-ink-900 font-semibold text-sm hover:brightness-110 transition"
        >
          <Plus size={14} /> Add question
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center text-white/50 text-sm py-16">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading FAQ…
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <FAQEditor
              key={it.id}
              item={it}
              index={i}
              total={items.length}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              onDelete={() => remove(it.id, it.question)}
              onMove={(dir) => move(i, dir)}
              onSaved={load}
            />
          ))}
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

function FAQEditor({ item, index, total, open, onToggle, onDelete, onMove, onSaved }) {
  const [q, setQ] = useState(item.question);
  const [a, setA] = useState(item.answer);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setQ(item.question); setA(item.answer); }, [item]);
  const dirty = q !== item.question || a !== item.answer;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/faq/${item.id}`, { question: q, answer: a, order: item.order });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`bo-faq-${item.id}`}
         className={`rounded-2xl border ${open ? "border-electric/50 bg-electric/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="flex flex-col text-white/40">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0}
                  data-testid={`bo-faq-up-${item.id}`}
                  className="disabled:opacity-20 hover:text-white">
            <ChevronUp size={14} />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={index === total - 1}
                  data-testid={`bo-faq-down-${item.id}`}
                  className="disabled:opacity-20 hover:text-white">
            <ChevronDown size={14} />
          </button>
        </div>
        <GripVertical size={14} className="text-white/20" />
        <button
          type="button"
          onClick={onToggle}
          data-testid={`bo-faq-toggle-${item.id}`}
          className="flex-1 text-left"
        >
          <div className={`font-serif text-base leading-snug ${open ? "text-electric" : "text-white"}`}>
            {item.question}
          </div>
        </button>
        <button type="button" onClick={onDelete}
                data-testid={`bo-faq-delete-${item.id}`}
                className="p-2 rounded-lg text-white/40 hover:text-red-300 hover:bg-red-500/10">
          <Trash2 size={14} />
        </button>
      </div>

      {open && (
        <div className="px-5 pb-5 pt-1 space-y-3">
          <label className="block">
            <span className="label-caps text-[10px] text-white/50">Question</span>
            <input
              type="text"
              className="mt-1 w-full bg-ink-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-electric/60"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid={`bo-faq-q-${item.id}`}
            />
          </label>
          <label className="block">
            <span className="label-caps text-[10px] text-white/50">Answer</span>
            <textarea
              rows={5}
              className="mt-1 w-full bg-ink-800 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-electric/60 leading-relaxed"
              value={a}
              onChange={(e) => setA(e.target.value)}
              data-testid={`bo-faq-a-${item.id}`}
            />
          </label>
          <div className="flex items-center justify-end gap-2">
            {!dirty && <span className="text-white/40 text-xs">Saved</span>}
            <button
              type="button"
              disabled={!dirty || saving}
              onClick={save}
              data-testid={`bo-faq-save-${item.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric text-ink-900 font-semibold text-xs disabled:opacity-40 hover:brightness-110"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
